import type { TxEvent } from 'polkadot-api'
import type { AssetHubChain } from '~/plugins/sdk.client'
import type { NFTMetadata } from '~/services/oda'
import type { TransactionError } from '~/utils/transactionError'
import { whenever } from '@vueuse/core'
import { resolveTransactionError } from '~/utils/transactionError'

export interface CollectionCategory {
  type: 'collection'
  id: string
  name: string
  description: string
  image: string
  hash: string
  prefix: AssetHubChain
}

export interface NftCategory {
  type: 'nft'
  collectionId: string
  collectionName: string
  hash: string
  items: {
    id: string
    name: string
    image: string
    price: number
    metadata: string
  }[]
  prefix: AssetHubChain
}

export interface ActionTransactionResult {
  type: 'listing' | 'buy' | 'burn' | 'token_transfer' | 'airdrop'
  items: {
    id: string
    sn: number
    price: number
    metadata: NFTMetadata
    metadata_uri: string
    collection: {
      id: number
      name: string
    }
  }[]
  hash: string
  prefix: AssetHubChain
}

export interface TransferTransactionResult {
  type: 'transfer'
  hash: string
  prefix: AssetHubChain
}

export interface CreateOfferTransactionResult {
  type: 'create_offer'
  hash: string
  prefix: AssetHubChain
}

export interface CreateSwapTransactionResult {
  type: 'create_swap'
  hash: string
  blockNumber: number
  prefix: AssetHubChain
}

export interface AcceptOfferTransactionResult {
  type: 'accept_offer'
  hash: string
  prefix: AssetHubChain
}

export interface CancelOfferTransactionResult {
  type: 'cancel_offer'
  hash: string
  prefix: AssetHubChain
}

export interface AcceptSwapTransactionResult {
  type: 'accept_swap'
  hash: string
  prefix: AssetHubChain
}

export interface CancelSwapTransactionResult {
  type: 'cancel_swap'
  hash: string
  prefix: AssetHubChain
}

export interface DestroyCollectionTransactionResult {
  type: 'collection_destroy'
  hash: string
  prefix: AssetHubChain
  collectionId: number
}

type TransactionResult
  = CollectionCategory
    | NftCategory
    | ActionTransactionResult
    | TransferTransactionResult
    | CreateOfferTransactionResult
    | AcceptOfferTransactionResult
    | CancelOfferTransactionResult
    | AcceptSwapTransactionResult
    | CancelSwapTransactionResult
    | CreateSwapTransactionResult
    | DestroyCollectionTransactionResult

// Transaction status progression:
// 1. status.value = 'signed'
// 2. status.value = 'broadcasted'
// 3. status.value = 'txBestBlocksState'
// 4. status.value = 'finalized'
const status = ref<TxEvent['type'] | null>(null)
const hash = ref('')
const rawError = ref<unknown | null>(null)
const error = computed<TransactionError | null, unknown | null>({
  get: () => (rawError.value ? resolveTransactionError(rawError.value) : null),
  set: (newValue) => {
    rawError.value = newValue
  },
})
const result = ref<TransactionResult | null>(null)
const open = ref(false)

export default function useTransactionModal() {
  const isSuccess = computed(() => status.value === 'finalized' && !error.value)
  const isError = computed(() => Boolean(error.value))

  function close() {
    open.value = false
    status.value = null
    hash.value = ''
    error.value = null
    result.value = null
  }

  function onSuccess<T extends TransactionResult>(type: T['type'], callback: (data: T) => void, { autoClose = true } = {}) {
    return whenever(() => isSuccess.value && type === result.value?.type, () => {
      const data = result.value as T
      autoClose && close()
      callback(data)
    })
  }

  return {
    // State
    hash,
    error,
    status,
    result,
    open,

    // Computed
    isSuccess,
    isError,

    // Methods
    close,
    onSuccess,
  }
}
